import React, { useState, useRef, useEffect, useMemo } from "react";
import { Model } from "../../../types/model";
import { useModelContext, getTierModel, AUTO_LOADING_MODEL } from "../../../contexts/ModelContext";
import { useAuth } from "../../../contexts/AuthContext";
import "../../../Styles/Layout/Model/ModelModal.css";
import { BulbIcon, SparklesIcon, Image01Icon, ZapIcon, StarIcon } from "hugeicons-react";
import { HoverToolTip } from "../Items/HoverToolTip";
import { CloseButton } from "../Buttons/CloseButton";
import { getModelIcon } from "../../../helpers/modelHelper";
import { useNavigate } from 'react-router-dom';
import {
  Sheet,
  SheetContent,
} from "../../ui/sheet";

const ModelItem = ({ model, onClick }: { model: Model, onClick: () => void }) => {
    return (
        <div className="model-item" onClick={onClick}>
            <div className="model-item-header">
                {getModelIcon(model)}
            </div>

            <div className="model-item-rating">

            </div>
            <span className="model-item-title">{model.displayName}</span>

            <div className="model-item-footer">
                <div className="model-item-footer-left">
                    {model.speed === 5 && (
                        <div className="model-footer-icon">
                            <ZapIcon size={18} className="model-footer-icon speed"/>
                            <HoverToolTip text="Blazing Speed" position="right" />
                        </div>
                    )}
                </div>
                <div className="model-item-footer-right">
                    {model.isReasoning && (
                        <>
                            <div className="model-item-footer-icon">
                            <BulbIcon size={18}/>
                            <HoverToolTip text="Reasoning Model" position="bottom" />
                            </div>
                        </>
                    )}
                    {model.canSeeImages && (
                        <>
                            <div className="model-item-footer-icon">
                                <Image01Icon size={18}/>
                                <HoverToolTip text="Has Vision" position="bottom" />
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    )
}

const ModelAutoItem = ({
    setCurrentModel,
    closeModelModal,
    autoModel,
}: {
    setCurrentModel: (model: Model) => void;
    closeModelModal: () => void;
    autoModel: Model;
}) => {
    return (
        <div className="model-item" onClick={() => {
            setCurrentModel(autoModel);
            closeModelModal();
        }}>
            <div className="model-item-header">
                <SparklesIcon size={24}/>
                <div className="recommended-model-indicator">
                        <StarIcon size={18}/>
                        <HoverToolTip text="Recommended" position="bottom" />
                </div>
            </div>
            <span className="model-item-title">{autoModel.displayName}</span>
            <div className="model-item-footer">
                <div className="model-item-footer-left">
                    <div className="model-item-footer-icon">
                        <ZapIcon size={18} className="model-item-footer-icon speed"/>
                        <HoverToolTip text="Blazing Speed" position="right" />
                    </div>
                </div>
                <div className="model-item-footer-right">
                </div>
            </div>
        </div>
    )
}

export const ModelModal = () => {
  const { isModelModalOpen, setIsModelModalOpen, currentModelSetter } = useModelContext();
  const navigate = useNavigate();
  const { modelRegistry, initialPayloadLoaded } = useAuth();
  const autoModel =
    getTierModel("auto", modelRegistry) ??
    (!initialPayloadLoaded ? AUTO_LOADING_MODEL : null);
  const displayedModels = useMemo(() => {
    if (!modelRegistry) return [];
    const seen = new Set<string>();
    return Object.values(modelRegistry).filter((model) => {
      if (seen.has(model.id)) return false;
      seen.add(model.id);
      return true;
    });
  }, [modelRegistry]);

  const handleModelSelect = (model: Model) => {
    currentModelSetter(model);
    setIsModelModalOpen(false);
  }

  const handleSeeAllModels = () => {
    navigate('/settings/model');
    setIsModelModalOpen(false);
  }

  const [isDragging, setIsDragging] = useState(false);
  const [startY, setStartY] = useState(0);
  const [currentY, setCurrentY] = useState(0);
  const [translateY, setTranslateY] = useState(0);
  const [ignoreOverlayClick, setIgnoreOverlayClick] = useState(false);
  const modalContainerRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isTouchDevice, setIsTouchDevice] = useState(false);

  const DRAG_THRESHOLD = 100; // Pixels to drag down to dismiss

  useEffect(() => {
    const handleResize = () => {
      if (typeof window !== "undefined") {
        setIsMobile(window.innerWidth <= 600);
      }
    };

    // Check if device has touch capability
    const checkTouchDevice = () => {
      if (typeof window !== "undefined") {
        setIsTouchDevice(
          'ontouchstart' in window || 
          navigator.maxTouchPoints > 0 || 
          (navigator as any).msMaxTouchPoints > 0
        );
      }
    };

    handleResize();
    checkTouchDevice();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
      if (e.touches.length !== 1) return; // Only handle single touch
      const touch = e.touches[0];
      setStartY(touch.clientY);
      setCurrentY(touch.clientY);
      setIsDragging(true);
      setIgnoreOverlayClick(false);
      if (modalContainerRef.current) {
          modalContainerRef.current.style.transition = 'none';
      }
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
      if (!isDragging || e.touches.length !== 1) return;
      const touch = e.touches[0];
      const deltaY = touch.clientY - startY;
      setCurrentY(touch.clientY); // Update current Y

      if (deltaY >= 0) {
          setTranslateY(deltaY);
      } else {
          setTranslateY(deltaY / 3);
      }
  };

  const handleTouchEnd = () => {
      if (!isDragging) return;

      setIsDragging(false);
      if (modalContainerRef.current) {
          modalContainerRef.current.style.transition = 'transform 0.3s ease-out'; // Re-enable transition
      }

      const deltaY = currentY - startY;

      // If there was a meaningful drag, ignore the synthetic click
      if (Math.abs(deltaY) > 5) {
          setIgnoreOverlayClick(true);
      }

      if (deltaY > DRAG_THRESHOLD) {
          setTranslateY(window.innerHeight);
          setTimeout(() => {
              setIsModelModalOpen(false);
              setTimeout(() => setTranslateY(0), 50);
          }, 300);
      } else {
          // Snap back to original position
          setTranslateY(0);
          // Allow overlay tap-to-close again after the snap animation
          setTimeout(() => {
              setIgnoreOverlayClick(false);
          }, 300);
      }
  };

  const handleOverlayClick = () => {
    // When coming from a drag gesture, a synthetic click fires on touchend.
    // We ignore the first such click so the slide animation can play smoothly.
    if (ignoreOverlayClick) {
      setIgnoreOverlayClick(false);
      return;
    }
    setIsModelModalOpen(false);
  };

  if (!isModelModalOpen && !isMobile) {
    return null;
  }

  // On mobile, render as a bottom sheet using the shadcn-style Sheet component
  if (isMobile) {
    return (
      <Sheet open={isModelModalOpen} onOpenChange={(open) => {
        // Only allow closing via overlay click if not dragging
        if (!open && !isDragging) {
          setIsModelModalOpen(false);
        }
      }}>
        <SheetContent
          ref={modalContainerRef}
          side="bottom"
          className="h-[90vh] rounded-t-3xl border-none bg-background p-0 flex flex-col overflow-hidden gap-0 [&[data-state=closed]]:duration-0 [&[data-state=open]]:duration-0 [&>button]:hidden"
          style={{ transform: `translateY(${translateY}px)`, transition: isDragging ? 'none' : 'transform 0.3s ease-out' }}
        >
          {isTouchDevice ? (
            // Touch device: show drag handle
            <div
              className="flex flex-col items-center pt-3 pb-2 px-4 cursor-grab active:cursor-grabbing"
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              onTouchCancel={handleTouchEnd}
              style={{ touchAction: 'none' }}
            >
              <div className="h-1.5 w-12 rounded-full bg-[var(--border-secondary)] mb-3" />
            </div>
          ) : (
            // Non-touch device: show close button with title
            <div className="flex flex-col items-center pt-3 pb-2 px-4 relative">
              <div className="absolute right-4 top-3">
                <CloseButton onClick={() => setIsModelModalOpen(false)} />
              </div>
            </div>
          )}
          <div className="model-list px-4 pt-2 pb-2">
            {autoModel && (
            <ModelAutoItem
              setCurrentModel={currentModelSetter}
              closeModelModal={() => {
                setIsModelModalOpen(false);
              }}
              autoModel={autoModel}
            />
            )}
            {displayedModels.map((model) => (
              <ModelItem
                key={model.id}
                model={model}
                onClick={() => handleModelSelect(model)}
              />
            ))}
          </div>
          <div className="model-list-footer px-4 pt-2 pb-2">
            <button className="model-list-footer-button" onClick={handleSeeAllModels}>
              <span>See All Models</span>
            </button>
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  // On desktop / larger screens, keep the existing centered modal with drag-to-dismiss
  return (
    <div
      className="modal-overlay"
      onClick={handleOverlayClick}
    >
      <div className="modal-header">
        <CloseButton onClick={() => {
          setIsModelModalOpen(false);
        }} />
      </div>
      <div
        ref={modalContainerRef} // Attach ref
        className="modal-container"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
        style={{ transform: `translateY(${translateY}px)` }}
      >
        <div className="model-list">
            {autoModel && (
            <ModelAutoItem
                setCurrentModel={currentModelSetter}
                closeModelModal={() => {
                  setIsModelModalOpen(false);
                }}
                autoModel={autoModel}
            />
            )}
            {displayedModels.map((model) => (
                <ModelItem
                    key={model.id}
                    model={model}
                    onClick={() => handleModelSelect(model)}
                />
            ))}
        </div>
        <div className="model-list-footer">
            <button className="model-list-footer-button" onClick={handleSeeAllModels}>
                <span>See All Models</span>
            </button>
        </div>
      </div>
    </div>
  );
};
